import { Route } from "react-router";
import { useTranslation } from "react-i18next";
import { EconomySettings } from "./EconomySettings";

import SettingsNavigation from "../../molecules/SettingsNavigation";

import UserSettings from "./User";
import UsersSettings from "./Users";
import CompanySpecificServices from "./Services";
import DepartmentSettings from "./DepartmentSettings";
import LogisticsSettings from "@/components/pages/Settings/LogisticsSettings";
import CompanySettings from "@/components/pages/Settings/CompanySettings";
import Templates from "@/components/pages/Settings/Templates";
import DeviationSettings from "@/components/pages/Settings/DeviationSettings";
import ContractArchive from "@/components/pages/Settings/ContractArchive";
import Customers from "@/components/pages/Settings/Customers";
import DocumentSettings from "@/components/pages/Settings/DocumentSettings";
import Product from "@/components/pages/Settings/Product";
import { Navigate, Routes } from "react-router-dom";
import { SidebarLayoutContainer, SidebarLayoutContent, SidebarLayoutMenu } from "@/components/atoms/SidebarLayout";

import Cameras from "./Cameras";
import TransportDevices from "./TransportDevices";
import ReceptionSettings from "./ReceptionSettings";
import BrandingSettings from "./BrandingSettings";
import { useIsWhiteLabel } from "@/services/WhiteLabel";

// Keeps /settings/branding unreachable by direct URL outside a white-label portal, where
// the page renders but every query inside it sits disabled without a tenant id.
const BrandingRoute = () => {
  const { isWhiteLabel, isSettled } = useIsWhiteLabel();

  // Hold off until the tenant is known: redirecting early would bounce a deep link on a
  // white-label domain straight to company settings on a hard refresh.
  if (!isSettled) return null;

  // Absolute path: a relative "company" would resolve against /settings/branding.
  return isWhiteLabel ? <BrandingSettings /> : <Navigate to="/settings/company" replace />;
};

const preloadComponents = {
  user: () => import("./User"),
  company: () => import("./CompanySettings"),
  users: () => import("./Users"),
  services: () => import("./Services"),
  economy: () => import("./EconomySettings"),
  logistics: () => import("./LogisticsSettings"),
  "transport-devices": () => import("./TransportDevices"),
  deviations: () => import("./DeviationSettings"),
  products: () => import("./Product"),
  customers: () => import("./Customers"),
  templates: () => import("./Templates"),
  document: () => import("./DocumentSettings"),
  cameras: () => import("./Cameras"),
  reception: () => import("./ReceptionSettings"),
};

export default function Settings() {
  const { t } = useTranslation();

  return (
    <>
      <title>{`Reviver - ${t("settings:settings")}`}</title>
      <meta name="description" content={`Reviver - ${t("settings:settings")}`} />

      <SidebarLayoutContainer padded>
        <SidebarLayoutMenu>
          <SettingsNavigation preloadComponents={preloadComponents} />
        </SidebarLayoutMenu>
        <SidebarLayoutContent stretch={false}>
          <Routes>
            <Route path="account" element={<UserSettings />} />
            <Route path={`company`} element={<CompanySettings />} />
            <Route path={`departments`} element={<DepartmentSettings />} />
            <Route path={`logistics`} element={<LogisticsSettings />} />
            <Route path={`transport-devices`} element={<TransportDevices />} />
            <Route path={`economy`} element={<EconomySettings />} />
            <Route path={`products`} element={<Product />} />
            <Route path={`services`} element={<CompanySpecificServices />} />
            <Route path={`deviation`} element={<DeviationSettings />} />
            {/* disabled untill reviver implementing open market <Route path={`network`} element={<NetworkSettings/>} /> */}
            <Route path={`users`} element={<UsersSettings />} />
            <Route path={`templates`} element={<Templates />} />
            <Route path={`customers`} element={<Customers />} />
            <Route path={`contract-archive`} element={<ContractArchive />} />
            <Route path={`documents`} element={<DocumentSettings />} />
            <Route path={`cameras`} element={<Cameras />} />
            <Route path={`reception`} element={<ReceptionSettings />} />
            <Route path="branding" element={<BrandingRoute />} />
            <Route path="/" element={<Navigate to="company" replace />} />
          </Routes>
        </SidebarLayoutContent>
      </SidebarLayoutContainer>
    </>
  );
}

export { SettingsFormContent, SettingsFormCTAs, SettingsHeader } from "./SettingsLayout";
